import { useCallback, useEffect, useRef } from "react";

// Portaled dialogs (createPortal to document.body) are appended after
// whatever triggered them in DOM order, so plain browser Tab handling either
// skips right past the dialog or, once inside it, walks straight back out
// into the page behind it. This parks focus on the dialog's own container
// the moment it opens - so the very first Tab lands on its first real
// control, matching NoteFiltersOverlay's existing pattern in Profiledetail.js
// - and then traps Tab/Shift+Tab inside its own focusable elements for as
// long as it stays open.
const DIALOG_FOCUSABLE_SELECTOR =
  'button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

// `active` only matters for dialogs that stay mounted the whole time (an
// `open` prop gates an internal `if (!open) return null` instead of the
// parent conditionally rendering the component) - pass that same `open`
// value so the focus effect re-fires on every reopen, not just the first
// time this component instance ever mounts. Dialogs the parent mounts fresh
// per open (the common case) don't need to pass anything.
export default function useDialogFocusTrap(active = true) {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!active || typeof window === "undefined") return undefined;

    // Remember whatever had focus right before this dialog opened (the
    // button/link that triggered it) so closing can hand focus back to it.
    // Without this, focus is simply lost the moment the dialog's DOM is
    // removed - document.activeElement falls back to <body> - and the next
    // Tab press restarts native tab order from the top of the page (the Dark
    // Mode button), instead of continuing from where the user actually was.
    triggerRef.current = document.activeElement;

    const focusContainer = window.requestAnimationFrame(() => containerRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(focusContainer);
      const trigger = triggerRef.current;
      if (trigger && typeof trigger.focus === "function" && document.body.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [active]);

  const onKeyDown = useCallback((event) => {
    if (event.key !== "Tab" || !containerRef.current) return;

    const focusable = Array.from(containerRef.current.querySelectorAll(DIALOG_FOCUSABLE_SELECTOR));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === containerRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  return { containerRef, onKeyDown };
}
